import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Flame,
  ChevronLeft,
  HandHeart,
  RefreshCw,
  Plus,
  X,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import Footer from '../components/Footer';

const PAYPAL_HOSTED_BUTTON_ID = 'GKVW63N7PCF6U';

const FUND_OPTIONS = [
  'Offering',
  'Tithes',
  'Outreach',
  'ACT Children',
  'Music',
  'Media',
  'Other',
];

const Give = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const preselectedFund = searchParams.get('fund');
  const initialFund =
    preselectedFund && FUND_OPTIONS.includes(preselectedFund)
      ? preselectedFund
      : 'Tithes';
  const [giftType, setGiftType] = useState('one-time');
  const [funds, setFunds] = useState([{ id: 1, fund: initialFund }]);

  const addFund = () => {
    setFunds((prev) => [...prev, { id: Date.now(), fund: 'Offering' }]);
  };

  const removeFund = (id) => {
    setFunds((prev) => (prev.length > 1 ? prev.filter((f) => f.id !== id) : prev));
  };

  const updateFund = (id, value) => {
    setFunds((prev) => prev.map((f) => (f.id === id ? { ...f, fund: value } : f)));
  };

  const handleNext = () => {
    if (funds.length === 0) {
      toast({ title: 'Pick a fund', description: 'Please choose at least one fund.' });
      return;
    }
    const uniqueFunds = [...new Set(funds.map((f) => f.fund))];
    const fundLabel = uniqueFunds.join(' + ');
    const customNote =
      (giftType === 'recurring' ? 'Recurring · ' : '') + fundLabel;

    const params = new URLSearchParams({
      hosted_button_id: PAYPAL_HOSTED_BUTTON_ID,
      custom: customNote,
      item_name: `ACT Gilroy \u2014 ${fundLabel}`,
    });

    window.open(
      `https://www.paypal.com/donate/?${params.toString()}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="min-h-screen bg-[#f4f2ef] flex flex-col">
      {/* Slim brand bar */}
      <header className="bg-[#0a1030] text-white">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
          >
            <Flame className="h-6 w-6" strokeWidth={2.5} />
            <span className="font-extrabold tracking-wide text-[14px] md:text-[15px]">
              APOSTOLIC CHRIST TABERNACLE
            </span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> Back to home
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://customer-assets-lxgj4vgw.emergentagent.net/job_laughing-proskuriakova-15/artifacts/3hm0dlnf_G08A0052.webp)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1030]/85 via-[#0a1445]/80 to-[#0a1030]/90" />
        <div className="relative z-10 max-w-[1000px] mx-auto text-center px-6 pt-16 pb-36 md:pt-20 md:pb-44">
          <p className="text-[12px] md:text-[13px] tracking-[0.3em] uppercase text-[#c8b98a] mb-4">
            Apostolic Christ Tabernacle — Gilroy
          </p>
          <h1
            className="text-white text-[54px] md:text-[84px] leading-none"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 400 }}
          >
            Give
          </h1>
          <p className="mt-6 text-white/85 text-[16px] md:text-[18px] max-w-[560px] mx-auto leading-relaxed">
            Thank you for supporting the mission of ACT. Choose your fund below,
            then enter your gift amount securely on PayPal.
          </p>
        </div>
      </section>

      {/* Overlapping card */}
      <section className="relative -mt-24 md:-mt-32 px-4 md:px-6 z-20">
        <div className="max-w-[720px] mx-auto bg-white rounded-2xl shadow-xl border border-black/5 p-6 md:p-10">
          {/* Gift type */}
          <div className="text-[15px] font-semibold text-[#1a1a1a] mb-3">
            Gift type
          </div>
          <div className="grid grid-cols-2 gap-3 mb-8">
            <button
              type="button"
              onClick={() => setGiftType('one-time')}
              className={`h-14 rounded-md border flex items-center justify-center gap-2 font-semibold transition-colors ${
                giftType === 'one-time'
                  ? 'bg-[#1c2b6b] border-[#1c2b6b] text-white'
                  : 'bg-white border-black/15 text-[#3a3a3a] hover:border-[#1c2b6b]'
              }`}
            >
              <HandHeart className="h-5 w-5" /> Give one time
            </button>
            <button
              type="button"
              onClick={() => setGiftType('recurring')}
              className={`h-14 rounded-md border flex items-center justify-center gap-2 font-semibold transition-colors ${
                giftType === 'recurring'
                  ? 'bg-[#1c2b6b] border-[#1c2b6b] text-white'
                  : 'bg-white border-black/15 text-[#3a3a3a] hover:border-[#1c2b6b]'
              }`}
            >
              <RefreshCw className="h-5 w-5" /> Set up recurring
            </button>
          </div>

          {/* Fund(s) */}
          <div className="text-[15px] font-semibold text-[#1a1a1a] mb-3">Fund</div>

          <div className="space-y-3">
            {funds.map((f) => (
              <div key={f.id} className="flex items-center gap-2">
                <Select value={f.fund} onValueChange={(v) => updateFund(f.id, v)}>
                  <SelectTrigger className="h-14 text-[15px] border-black/15 rounded-md">
                    <SelectValue placeholder="Select a fund" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUND_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} className="py-3 text-[15px]">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {funds.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFund(f.id)}
                    className="h-10 w-10 flex items-center justify-center rounded-full text-[#8a8a8a] hover:bg-red-50 hover:text-red-600 transition-colors"
                    aria-label="Remove fund"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={addFund}
              className="inline-flex items-center gap-1 rounded-full border border-[#1c2b6b] text-[#1c2b6b] px-5 py-2 text-[14px] font-medium hover:bg-[#1c2b6b]/5 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add another fund
            </button>
          </div>

          {giftType === 'recurring' && (
            <p className="mt-6 text-xs text-[#7a7a7a] text-center">
              PayPal will guide you to set up your recurring gift on the next screen.
            </p>
          )}
        </div>

        <div className="max-w-[720px] mx-auto mt-6 flex justify-center">
          <Button
            onClick={handleNext}
            className="rounded-full bg-[#1c2b6b] hover:bg-[#141f52] text-white h-14 px-16 text-[16px] font-semibold shadow-md"
          >
            Continue to PayPal
          </Button>
        </div>

        <div className="max-w-[720px] mx-auto mt-6 flex items-center justify-center gap-2 text-xs text-[#6a6a6a] text-center">
          <ShieldCheck className="h-4 w-4" />
          You'll enter your gift amount securely on PayPal · card details never touch our site
        </div>
      </section>

      <div className="flex-1" />

      <div className="pt-24">
        <Footer />
      </div>
      <Toaster />
    </div>
  );
};

export default Give;
